import { useMutationWithToast } from '@/hooks/useQueryWithToast';
import { supabase } from '@/integrations/supabase/client';

export interface CopyProjectDataOptions {
  sourceProjectId: string;
  targetProjectId: string;
  copyItems: boolean;
  copySpecs: boolean;
  copyDrawings: boolean;
  skipDuplicates: boolean;
  copyLayersPins: boolean;
  layerIds?: string[] | null; // null = all layers, array = specific layers
  specIds?: string[] | null;  // NEW: null = all specs, array = specific specs (Option B: overrides layerIds)
}

export interface CopyProjectDataResult {
  success: boolean;
  batch_id?: string;
  items_copied?: number;
  gates_copied?: number;
  milestones_copied?: number;
  milestones_clamped?: number;
  specs_copied?: number;
  spec_files_copied?: number;
  drawings_copied?: number;
  layers_copied?: number;
  pins_copied?: number;
  pins_linked?: number;
  items_dates_cleared?: number;
  milestones_dates_cleared?: number;
  error?: string;
}

export function useCopyProjectData() {
  return useMutationWithToast<CopyProjectDataResult, CopyProjectDataOptions>({
    mutationFn: async (options): Promise<CopyProjectDataResult> => {
      // Build RPC params - use type assertion for new parameter until types regenerate
      const rpcParams: Record<string, unknown> = {
        p_source_project_id: options.sourceProjectId,
        p_target_project_id: options.targetProjectId,
        p_copy_items: options.copyItems,
        p_copy_specs: options.copySpecs,
        p_copy_drawings: options.copyDrawings,
        p_skip_duplicates: options.skipDuplicates,
        p_copy_layers_pins: options.copyLayersPins,
        p_layer_ids: options.layerIds ?? null,
        p_spec_ids: options.specIds ?? null, // NEW: selective spec copying (Option B)
      };
      
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)('copy_project_data', rpcParams);
      
      if (error) throw error;
      
      const result = data as unknown as CopyProjectDataResult;
      if (!result.success) {
        throw new Error(result.error || 'Copy failed');
      }
      
      return result;
    },
    successMessage: 'Đã copy dữ liệu thành công',
    errorMessage: 'Lỗi khi copy dữ liệu',
    invalidateQueries: [
      ['specs-data'],
      ['optimized-specs-data'],
      ['specs-layers'],
      ['specs-pins'],
      ['diary-pins'],
      ['items-optimized'],
      ['items'],
      ['project-items'],
      ['drawings'],
    ],
  });
}
